import { useCallback, useState } from 'react'
import { Activity, AlertTriangle, CheckCircle2, XCircle, Check, X } from 'lucide-react'
import AdminScreenShell, { StatusRij, Tegel } from '@/components/AdminScreenShell'
import { adminAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

/* Health-overzicht — één scherm voor de LAN-avond dat áls-eerste rood wordt
   zodra iets niet meer werkt. Combineert:
     - system-health snapshot (containers, disks, DB, services, certs)
     - open incidents-lijst (backend hang, disk drempels, kassa down, …)
   De data-poll is 15s (default van shell); de backend-poller die de
   incidents oplevert draait op 30s. Nieuw incident is dus max 45s zichtbaar. */

const OK  = '#61E294'
const WRN = '#FFB57A'
const ERR = '#FF5A5A'

const kleurVoorLevel = (lvl) => lvl === 'critical' ? ERR : lvl === 'warning' ? WRN : OK

function fmtLeeftijd(iso) {
  if (!iso) return ''
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return `${s}s geleden`
  if (s < 3600) return `${Math.round(s / 60)}m geleden`
  if (s < 86400) return `${Math.round(s / 3600)}u geleden`
  return `${Math.round(s / 86400)}d geleden`
}

export default function AdminHealthScreen() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [health, setHealth]       = useState(null)
  const [incidents, setIncidents] = useState({ incidents: [], counts: {} })
  const [err, setErr]             = useState(null)
  const [busyIds, setBusyIds]     = useState(new Set())

  const load = useCallback(async () => {
    try {
      const [h, i] = await Promise.all([
        adminAPI.getSystemHealth(),
        adminAPI.listIncidents('open', 30),
      ])
      setHealth(h.data)
      setIncidents(i.data)
      setErr(null)
    } catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }, [])

  const withBusy = async (id, fn) => {
    setBusyIds(prev => new Set(prev).add(id))
    try {
      await fn()
      await load()
    } catch (e) { alert('Actie mislukt: ' + (e.response?.data?.error || e.message)) }
    finally {
      setBusyIds(prev => { const n = new Set(prev); n.delete(id); return n })
    }
  }
  const acknowledge = (id) => withBusy(id, () => adminAPI.acknowledgeIncident(id))
  const resolve     = (id) => withBusy(id, () => adminAPI.resolveIncident(id))

  const containers = Array.isArray(health?.docker)  ? health.docker  : []
  const disks      = Array.isArray(health?.disks)   ? health.disks   : []
  const services   = Array.isArray(health?.services)? health.services: []
  const certs      = Array.isArray(health?.certs)   ? health.certs   : []
  const pg         = health?.postgres && !health.postgres.error ? health.postgres : null

  const open = incidents.incidents ?? []
  const counts = incidents.counts ?? {}
  const critical = counts.critical_open ?? 0
  const warning  = counts.warning_open ?? 0

  const overallKleur = critical > 0 ? ERR : warning > 0 ? WRN : OK
  const overallTxt   = critical > 0 ? 'KRITIEK' : warning > 0 ? 'AANDACHT' : 'ALLES OK'

  return (
    <AdminScreenShell
      title="Health"
      subtitle="Live-overzicht kritieke systemen · rood eerst"
      icon={Activity}
      accent={overallKleur}
      refreshMs={15_000}
      onRefresh={load}
    >
      {err && (
        <div className="rounded-[6px] border border-signal/50 bg-signal/10 p-4 mb-4 text-signal">
          Kon health niet ophalen: {err}
        </div>
      )}

      {/* Kop-tegels — één blik = ken je toestand. */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <Tegel label="Status"     waarde={overallTxt}
               kleur={overallKleur} groot />
        <Tegel label="Open · kritiek"  waarde={critical}     kleur={critical ? ERR : OK} />
        <Tegel label="Open · aandacht" waarde={warning}      kleur={warning  ? WRN : OK} />
        <Tegel label="Laatste 24u"     waarde={counts.last_24h ?? 0}
               kleur="#B4FF39" sub="totale detecties" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Open incidenten — belangrijkste blok, dus links en groot. */}
        <div className="rounded-[6px] border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              Open incidenten
            </h2>
            <span className="font-mono text-[10px] text-muted-foreground">
              {open.length} van {counts.total_open ?? 0}
            </span>
          </div>
          {open.length === 0 ? (
            <div className="flex items-center gap-3 p-4 rounded-[4px] border border-border/40 bg-background">
              <CheckCircle2 className="h-6 w-6" style={{ color: OK }} />
              <div>
                <div className="text-sm font-semibold">Geen open incidenten</div>
                <div className="text-xs text-muted-foreground">
                  De poller loopt elke 30 s. Nieuwe problemen verschijnen hier automatisch.
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {open.map(inc => {
                const kl = kleurVoorLevel(inc.level)
                const Icon = inc.level === 'critical' ? XCircle : AlertTriangle
                const busy = busyIds.has(inc.id)
                return (
                  <div key={inc.id}
                       className="rounded-[4px] border border-border/60 bg-background p-3"
                       style={{ borderLeft: `3px solid ${kl}` }}>
                    <div className="flex items-start gap-2">
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: kl }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">{inc.title}</div>
                        {inc.detail && (
                          <div className="text-xs text-muted-foreground mt-0.5 break-words">
                            {inc.detail}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          <span>{inc.source}</span>
                          <span>·</span>
                          <span>{fmtLeeftijd(inc.last_seen)}</span>
                          {inc.occurrences > 1 && (
                            <>
                              <span>·</span>
                              <span>{inc.occurrences}× gezien</span>
                            </>
                          )}
                          {inc.acknowledged_at && (
                            <>
                              <span>·</span>
                              <span style={{ color: OK }}>
                                ✓ {inc.acknowledged_by ?? 'admin'}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Reset-knoppen — admin sluit het incident handmatig.
                          Ack = gezien maar nog niet opgelost (blijft in de
                          lijst met ✓ badge). Opgelost = sluit het incident
                          (zelfde als de auto-resolver wanneer de conditie weg is). */}
                      {isAdmin && (
                        <div className="flex flex-col gap-1 shrink-0">
                          {!inc.acknowledged_at && (
                            <button
                              onClick={() => acknowledge(inc.id)}
                              disabled={busy}
                              title="Gezien — houdt open maar markeert als bekend"
                              className="p-1.5 rounded border border-border hover:border-yellow-500 hover:text-yellow-400 transition-colors disabled:opacity-40">
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (confirm(`"${inc.title}" markeren als opgelost?\n\nAls de conditie later terugkomt, verschijnt het incident automatisch weer.`))
                                resolve(inc.id)
                            }}
                            disabled={busy}
                            title="Opgelost — sluit het incident"
                            className="p-1.5 rounded border border-border hover:border-emerald-500 hover:text-emerald-400 transition-colors disabled:opacity-40">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Rechter kolom: live-checks, condenseerd. */}
        <div className="flex flex-col gap-4">

          {/* Postgres */}
          <div className="rounded-[6px] border border-border bg-card p-4">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
              Database
            </h2>
            {pg ? (
              <div className="grid grid-cols-3 gap-3">
                <Tegel label="Verbindingen" waarde={pg.active_connections ?? '—'} kleur={OK} />
                <Tegel label="DB-grootte"    waarde={pg.db_size ?? '—'} kleur="#B4FF39" />
                <Tegel label="Uptime"        waarde={pg.uptime_seconds ? `${Math.round(pg.uptime_seconds/3600)}u` : '—'}
                       kleur="#B4FF39" />
              </div>
            ) : (
              <div className="text-signal font-semibold">Onbereikbaar</div>
            )}
          </div>

          {/* Services */}
          <div className="rounded-[6px] border border-border bg-card p-4">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
              Services
            </h2>
            {services.map(s => (
              <StatusRij key={s.name}
                         naam={s.name}
                         sub={s.error ? s.error : `${s.status ?? '?'} · ${s.latency_ms ?? '?'} ms`}
                         status={s.reachable ? 'ok' : 'fout'} />
            ))}
          </div>

          {/* Containers */}
          <div className="rounded-[6px] border border-border bg-card p-4">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
              Containers
            </h2>
            {containers.length === 0 ? (
              <div className="text-xs text-muted-foreground">Geen container-data.</div>
            ) : containers.map(c => (
              <StatusRij key={c.name} naam={c.name} sub={c.status}
                         status={c.healthy ? 'ok' : 'fout'} />
            ))}
          </div>

          {/* Disks */}
          <div className="rounded-[6px] border border-border bg-card p-4">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
              Disk-gebruik
            </h2>
            {disks.map(d => {
              const st = d.used_pct >= 90 ? 'fout' : d.used_pct >= 80 ? 'warn' : 'ok'
              return (
                <StatusRij key={d.mount} naam={d.mount}
                           sub={`${d.used_gb} / ${d.total_gb} GB`}
                           status={`${d.used_pct}%`}
                           kleur={st === 'fout' ? ERR : st === 'warn' ? WRN : OK} />
              )
            })}
          </div>

          {/* Certs — geen alarm, maar wel bij ~14 dagen. */}
          {certs.length > 0 && (
            <div className="rounded-[6px] border border-border bg-card p-4">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
                TLS-certificaten
              </h2>
              {certs.map(c => (
                <StatusRij key={c.path} naam={c.subject || c.path}
                           sub={`vervalt ${new Date(c.expires_at).toLocaleDateString('nl-BE')}`}
                           status={`${c.days_until}d`}
                           kleur={c.days_until > 14 ? OK : c.days_until > 0 ? WRN : ERR} />
              ))}
            </div>
          )}

        </div>
      </div>
    </AdminScreenShell>
  )
}
